import {
  parseSpeakerTurns,
  type SpeakerAssignment,
  type TranscriptFlagItem,
  type TranscriptSegmentItem,
} from "@rosc/contracts";
import type { JSX } from "react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAudioEngine } from "@/lib/review/audio-engine";
import { formatMoment } from "@/lib/review/noteSources";
import { cn } from "@/lib/utils";
import { useSpeakerLabel } from "./SpeakerTranscript";
import { FlaggedText } from "./TranscriptFlags";

// The transcript as timed segments (v3.2 drawer): speaker, time and words
// per segment. Tapping a segment plays the recording from there; the one
// playing is highlighted (segment level: word timings are not stored) and
// followed by the scroll unless the clinician scrolled in the last few
// seconds.

const FOLLOW_PAUSE_MS = 4000;

// The flags whose quote occurs in this segment, with offsets in it.
const flagsFor = (
  text: string,
  flags: readonly TranscriptFlagItem[],
): TranscriptFlagItem[] =>
  flags.flatMap((flag) => {
    const start = text.indexOf(flag.quote);
    return start < 0
      ? []
      : [{ ...flag, start, end: start + flag.quote.length }];
  });

export const SegmentTranscript = ({
  segments,
  transcript,
  audioPath,
  flags = [],
}: {
  segments: readonly TranscriptSegmentItem[];
  // The stored transcript, for the speakers' roles.
  transcript: string;
  audioPath: string | null;
  flags?: readonly TranscriptFlagItem[];
}): JSX.Element => {
  const { t } = useTranslation();
  const label = useSpeakerLabel();
  const roles = useMemo(() => {
    const map = new Map<number, SpeakerAssignment | null>();
    for (const turn of parseSpeakerTurns(transcript))
      if (turn.speaker !== null && !map.get(turn.speaker))
        map.set(turn.speaker, turn.assignment);
    return map;
  }, [transcript]);
  const ours = useAudioEngine((state) => state.path === audioPath);
  const nowMs = useAudioEngine((state) =>
    state.path === audioPath ? state.currentTime * 1000 : -1,
  );
  const playing = useAudioEngine(
    (state) => state.path === audioPath && state.playing,
  );
  let active = -1;
  if (ours && nowMs >= 0)
    for (const [index, segment] of segments.entries())
      if (segment.startMs <= nowMs + 50) active = index;
      else break;
  const list = useRef<HTMLOListElement>(null);
  const userScrolled = useRef(0);
  useEffect(() => {
    if (active < 0 || !playing) return;
    if (Date.now() - userScrolled.current < FOLLOW_PAUSE_MS) return;
    list.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [active, playing]);
  const playFrom = (startMs: number): void => {
    if (!audioPath) return;
    const engine = useAudioEngine.getState();
    void engine.load(audioPath).then((ok) => {
      if (!ok) return;
      engine.seek(Math.max(0, startMs - 150) / 1000);
      void engine.play();
    });
  };
  const markScrolled = (): void => {
    userScrolled.current = Date.now();
  };

  return (
    <ol
      ref={list}
      aria-label={t("visit.player.segments")}
      className="space-y-0.5 font-v3"
      data-testid="segment-transcript"
      onTouchMove={markScrolled}
      onWheel={markScrolled}
    >
      {segments.map((segment, index) => {
        const isActive = index === active;
        return (
          <li key={segment.idx} data-index={index}>
            {/* The row plays from its start on a tap anywhere (the text may
                hold flag marks, which are their own buttons); the time is
                the keyboard-accessible control. */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: the time button inside is the accessible control for the same action */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: as above */}
            <div
              className={cn(
                "grid cursor-pointer grid-cols-[auto_1fr] gap-x-3 rounded-[12px] px-3 py-2.5 transition-colors",
                isActive ? "bg-v3-brand-tint" : "hover:bg-v3-surface-2",
              )}
              data-active={isActive || undefined}
              data-testid="transcript-segment"
              onClick={() => playFrom(segment.startMs)}
            >
              <button
                aria-current={isActive ? "true" : undefined}
                aria-label={t("visit.player.playFrom", {
                  time: formatMoment(segment.startMs),
                })}
                className="flex min-w-[64px] flex-col items-start rounded-md pt-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:cursor-default"
                disabled={!audioPath}
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  playFrom(segment.startMs);
                }}
              >
                <span className="text-[12px] font-semibold text-v3-text-2">
                  {segment.speaker === null
                    ? ""
                    : label(
                        segment.speaker,
                        roles.get(segment.speaker) ?? null,
                      )}
                </span>
                <span
                  className={cn(
                    "font-mono text-[11px] tabular-nums",
                    isActive ? "text-v3-brand-strong" : "text-v3-text-3",
                  )}
                >
                  {formatMoment(segment.startMs)}
                </span>
              </button>
              <span
                className={cn(
                  "text-[15px] leading-[1.55]",
                  isActive ? "text-v3-text" : "text-v3-text-2",
                )}
              >
                <FlaggedText
                  flags={flagsFor(segment.text, flags)}
                  text={segment.text}
                />
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
};
